/**
 * Mandate form model: the display-layer state of the wizard and its conversion
 * to the wire format.
 *
 * UNIT RULE (SPEC.md principle 5): percentages exist ONLY here. The form holds
 * percent strings ("10"); `formToBody` converts them to FRACTIONS (0.10) before
 * anything is validated or sent, and `bodyToForm` converts back for display.
 * Sending a whole-number percent to the API is a validation error, so no code
 * outside this file should ever hold a percent.
 *
 * Nothing here calls the network. Everything is a pure function so the dollar
 * previews the user reads are provably the same numbers that get enforced
 * (acceptance test C5: preview = ratio x capital).
 */

import type {
  AgentAction,
  AutonomyLevel,
  DigestCadence,
  MandateBody,
  Violation,
} from './types';
import { REQUIRED_ACK_DOCS, REQUIRED_NEVER } from './validate';

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

export const VENUE_OPTIONS: Array<{ id: string; label: string }> = [
  { id: 'alpaca', label: 'Alpaca (US stocks and ETFs)' },
  { id: 'kraken', label: 'Kraken (crypto)' },
  { id: 'oanda', label: 'OANDA (forex)' },
];

export const ASSET_CLASS_OPTIONS: Array<{ id: string; label: string }> = [
  { id: 'us_etf', label: 'US ETFs' },
  { id: 'us_equity', label: 'US stocks' },
  { id: 'crypto_spot', label: 'Crypto (spot)' },
  { id: 'fx', label: 'Forex' },
];

export const ALERT_CHANNEL_OPTIONS: Array<{ id: string; label: string }> = [
  { id: 'email', label: 'Email' },
  { id: 'slack', label: 'Slack' },
];

/** Actions the user can grant, with the plain-language consequence of each. */
export const AGENT_ACTION_OPTIONS: Array<{
  id: AgentAction;
  label: string;
  detail: string;
  /** Moves or sizes positions, so it needs autonomy level L2 or higher. */
  order: boolean;
}> = [
  { id: 'place_orders', label: 'Place orders', detail: 'Buy and sell inside your limits.', order: true },
  { id: 'cancel_orders', label: 'Cancel orders', detail: 'Pull orders that have not filled.', order: true },
  {
    id: 'resize_within_limits',
    label: 'Resize positions',
    detail: 'Make positions bigger or smaller, never past a limit.',
    order: true,
  },
  { id: 'rebalance', label: 'Rebalance', detail: 'Put holdings back to their target shares on each rule’s schedule.', order: true },
  { id: 'flatten', label: 'Sell everything to cash', detail: 'Close all positions (used at the last safety step).', order: true },
  { id: 'add_strategy', label: 'Add a rule', detail: 'Bring a new rule into the plan.', order: false },
  { id: 'remove_strategy', label: 'Remove a rule', detail: 'Drop a rule from the plan.', order: false },
  { id: 'raise_target_risk', label: 'Raise the risk target', detail: 'Take more risk than the last approved plan.', order: false },
];

export const ORDER_ACTION_IDS: AgentAction[] = AGENT_ACTION_OPTIONS.filter((a) => a.order).map((a) => a.id);

/** What the agent can never do, no matter what is written; shown locked. */
export const NEVER_OPTIONS: Array<{ id: AgentAction; label: string }> = [
  { id: 'change_limits', label: 'Change your limits' },
  { id: 'resume_after_halt', label: 'Resume trading after a shut-off' },
  { id: 'change_credentials', label: 'Change broker keys' },
  { id: 'withdraw', label: 'Withdraw money' },
];

/** Plain-language name of any agent action, including the always-allowed halt. */
export function actionLabel(id: string): string {
  if (id === 'halt') return 'Stop trading';
  return (
    AGENT_ACTION_OPTIONS.find((o) => o.id === id)?.label ??
    NEVER_OPTIONS.find((o) => o.id === id)?.label ??
    id
  );
}

export type ActionPolicy = 'may' | 'ask' | 'deny';

export const AUTONOMY_LEVELS: Array<{ id: AutonomyLevel; label: string; detail: string; available: boolean }> = [
  { id: 'L0', label: 'L0 Suggest only', detail: 'The agent proposes; you do everything.', available: true },
  { id: 'L1', label: 'L1 Assisted', detail: 'The agent prepares actions; you confirm each one.', available: true },
  { id: 'L2', label: 'L2 Paper-autonomous', detail: 'The agent trades on its own inside your limits.', available: true },
  {
    id: 'L3',
    label: 'L3 Live-autonomous',
    detail: 'Needs a passing certification of the agent on the release in use. Not available yet.',
    available: false,
  },
];

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

export type TemplateId = 'conservative' | 'balanced' | 'custom';

export interface MandateFormState {
  template: TemplateId;
  /** Country or country-region code, e.g. "US-GA". */
  jurisdiction: string;
  /** Dollars, digits and an optional decimal point. */
  capital: string;
  /** Percent of equity kept in cash. */
  minCashReserve: string;
  venues: string[];
  assetClasses: string[];
  /** Comma- or space-separated tickers. */
  instrumentAllow: string;
  shorting: boolean;
  derivatives: boolean;
  /** Gross leverage as a multiple, "1" = none. */
  leverage: string;
  /** Percent of equity per instrument. */
  maxPosition: string;
  /** Percent of equity per asset class; '' = no separate cap. */
  assetClassCaps: Record<string, string>;
  /** Percent of equity, total exposure. */
  maxGross: string;
  /** Percent of equity, long minus short. */
  maxNet: string;
  /** Largest single order as a percent of the allocated capital. */
  maxOrderPct: string;
  maxOrdersPerDay: string;
  /** Percent of equity traded per day. */
  maxTurnover: string;
  /** Percent of the day's starting equity. */
  dailyLoss: string;
  rung1At: string;
  /** Percent of position size that remains after the first rung. */
  rung1Scale: string;
  rung2At: string;
  level: AutonomyLevel;
  policies: Record<string, ActionPolicy>;
  digest: DigestCadence;
  alertChannels: string[];
}

const BALANCED_POLICIES: Record<string, ActionPolicy> = {
  place_orders: 'may',
  cancel_orders: 'may',
  resize_within_limits: 'may',
  rebalance: 'may',
  flatten: 'may',
  add_strategy: 'ask',
  remove_strategy: 'ask',
  raise_target_risk: 'ask',
};

const CONSERVATIVE_POLICIES: Record<string, ActionPolicy> = {
  place_orders: 'ask',
  cancel_orders: 'ask',
  resize_within_limits: 'ask',
  rebalance: 'ask',
  flatten: 'ask',
  add_strategy: 'ask',
  remove_strategy: 'ask',
  raise_target_risk: 'ask',
};

/**
 * Template values. Every template leaves `capital` empty on purpose: the amount
 * is the one number the user must state, and activation stays blocked until they
 * do. `balanced` is the ladder from SPEC.md section 2 (shrink at 10%, shut off
 * at 20%); `conservative` halves each threshold and asks before every action.
 */
export function templateForm(template: Exclude<TemplateId, 'custom'>): MandateFormState {
  const common = {
    jurisdiction: 'US',
    capital: '',
    venues: ['alpaca', 'kraken'],
    assetClasses: ['us_etf', 'crypto_spot'],
    instrumentAllow: 'SPY, EFA, IEF, VNQ, DBC, BTC/USD, ETH/USD',
    shorting: false,
    derivatives: false,
    leverage: '1',
    maxGross: '100',
    maxNet: '100',
    digest: 'daily' as DigestCadence,
    alertChannels: ['email'],
  };
  if (template === 'conservative') {
    return {
      ...common,
      template,
      minCashReserve: '10',
      maxPosition: '15',
      assetClassCaps: { crypto_spot: '20' },
      maxOrderPct: '15',
      maxOrdersPerDay: '10',
      maxTurnover: '25',
      dailyLoss: '2',
      rung1At: '5',
      rung1Scale: '50',
      rung2At: '10',
      level: 'L1',
      policies: { ...CONSERVATIVE_POLICIES },
    };
  }
  return {
    ...common,
    template,
    minCashReserve: '5',
    maxPosition: '25',
    assetClassCaps: { crypto_spot: '60' },
    maxOrderPct: '30',
    maxOrdersPerDay: '20',
    maxTurnover: '50',
    dailyLoss: '3',
    rung1At: '10',
    rung1Scale: '50',
    rung2At: '20',
    level: 'L2',
    policies: { ...BALANCED_POLICIES },
  };
}

export function defaultForm(): MandateFormState {
  return templateForm('balanced');
}

// ---------------------------------------------------------------------------
// Percent <-> fraction (the ONLY place this conversion happens)
// ---------------------------------------------------------------------------

const NUMBER_RE = /^\d+(\.\d+)?$|^\.\d+$/;

/** True when the text is a plain non-negative decimal number. */
export function isPlainNumber(text: string): boolean {
  return NUMBER_RE.test(text.trim());
}

/**
 * "10" -> 0.10, "2.5" -> 0.025, using decimal-exponent parsing so no binary
 * rounding noise creeps in (0.1/100 would give 0.0010000000000000002). Returns
 * NaN for anything that is not a plain non-negative number.
 */
export function percentToFraction(percent: string): number {
  const t = percent.trim();
  if (!isPlainNumber(t)) return Number.NaN;
  return Number(`${t}e-2`);
}

/** 0.10 -> "10", 0.025 -> "2.5"; the inverse of percentToFraction. */
export function fractionToPercent(fraction: number): string {
  if (!Number.isFinite(fraction)) return '';
  return String(Number(`${fraction}e2`));
}

function plainNumber(text: string): number {
  const t = text.trim();
  return isPlainNumber(t) ? Number(t) : Number.NaN;
}

function wholeNumber(text: string): number {
  const t = text.trim();
  return /^\d+$/.test(t) ? Number(t) : Number.NaN;
}

/** "SPY, EFA  BTC/USD" -> ["SPY","EFA","BTC/USD"] (upper-cased, de-duplicated). */
export function parseTickerList(text: string): string[] {
  const out: string[] = [];
  for (const raw of text.split(/[\s,;]+/)) {
    const t = raw.trim().toUpperCase();
    if (t !== '' && !out.includes(t)) out.push(t);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

const usd0 = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
const usd2 = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** $5,000 for whole dollars, $1,234.50 otherwise. */
export function formatUsd(amount: number): string {
  if (!Number.isFinite(amount)) return '$–';
  return Math.abs(amount - Math.round(amount)) < 0.005 ? usd0.format(Math.round(amount)) : usd2.format(amount);
}

/** Dollars from a capital amount and a fraction of it, rounded to cents. */
export function dollarsOf(capital: number, fraction: number): number {
  return Math.round(capital * fraction * 100) / 100;
}

/** Decimal string with two places for the wire, e.g. 1500 -> "1500.00". */
export function moneyString(amount: number): string {
  return amount.toFixed(2);
}

/** Capital typed into the form, in dollars; NaN when blank or not a number. */
export function capitalOf(form: MandateFormState): number {
  const n = plainNumber(form.capital.replace(/[$,\s]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : Number.NaN;
}

// ---------------------------------------------------------------------------
// Form -> wire body
// ---------------------------------------------------------------------------

/** Who and when to record on the acknowledgements (the server re-stamps both). */
export interface SigningMeta {
  by: string;
  at: string;
  docVersion: string;
  /** Docs the user ticked. */
  acknowledged: string[];
}

export const CURRENT_DOC_VERSION = '2026-10';

export interface FormConversion {
  body: MandateBody;
  /** Problems with what was typed (blank, not a number) keyed by validator field. */
  issues: Violation[];
}

/**
 * Build the wire body from the form. Unreadable numbers become NaN in the body
 * (the validator then flags them) and are also listed in `issues` with a plain
 * message so the form can say "enter a number" rather than a rule name.
 */
export function formToBody(form: MandateFormState, meta: SigningMeta): FormConversion {
  const issues: Violation[] = [];
  const pct = (text: string, field: string): number => {
    const f = percentToFraction(text);
    if (Number.isNaN(f)) issues.push({ field, message: 'Enter a number, for example 10.' });
    return f;
  };
  const num = (text: string, field: string): number => {
    const n = plainNumber(text);
    if (Number.isNaN(n)) issues.push({ field, message: 'Enter a number.' });
    return n;
  };

  const capital = capitalOf(form);
  if (Number.isNaN(capital)) {
    issues.push({ field: 'capital.allocated.amount', message: 'Enter the amount of money the agent may manage, in dollars.' });
  }
  const maxOrderFraction = pct(form.maxOrderPct, 'exposure.max_order_notional.amount');
  const orderAmount = Number.isNaN(capital) || Number.isNaN(maxOrderFraction) ? Number.NaN : dollarsOf(capital, maxOrderFraction);

  const maxOrders = wholeNumber(form.maxOrdersPerDay);
  if (Number.isNaN(maxOrders)) {
    issues.push({ field: 'exposure.max_orders_per_day', message: 'Enter a whole number of orders.' });
  }

  const maxAssetClass: Record<string, number> = {};
  for (const cls of form.assetClasses) {
    const raw = (form.assetClassCaps[cls] ?? '').trim();
    if (raw === '') continue;
    maxAssetClass[cls] = pct(raw, `exposure.max_asset_class.${cls}`);
  }

  const scale = pct(form.rung1Scale, 'loss.drawdown_ladder[0].scale');

  const may: AgentAction[] = ['halt'];
  const needsConfirmation: AgentAction[] = [];
  for (const opt of AGENT_ACTION_OPTIONS) {
    const p = form.policies[opt.id] ?? 'deny';
    if (p === 'may') may.push(opt.id);
    else if (p === 'ask') needsConfirmation.push(opt.id);
  }

  const ack = (doc: string) => ({ doc, doc_version: meta.docVersion, at: meta.at, by: meta.by });

  const body: MandateBody = {
    basis: {
      capital_source: 'own',
      jurisdiction: form.jurisdiction.trim(),
      acknowledgements: REQUIRED_ACK_DOCS.filter((d) => meta.acknowledged.includes(d)).map(ack),
    },
    capital: {
      allocated: { amount: Number.isNaN(capital) ? '' : moneyString(capital), ccy: 'USD' },
      min_cash_reserve: pct(form.minCashReserve, 'capital.min_cash_reserve'),
    },
    universe: {
      venues: [...form.venues],
      asset_classes: [...form.assetClasses],
      instrument_allow: parseTickerList(form.instrumentAllow),
      instrument_deny: [],
      shorting: form.shorting,
      derivatives: form.derivatives,
      leverage_max_gross: num(form.leverage, 'universe.leverage_max_gross'),
    },
    exposure: {
      max_position: pct(form.maxPosition, 'exposure.max_position'),
      max_asset_class: maxAssetClass,
      max_gross: pct(form.maxGross, 'exposure.max_gross'),
      max_net: pct(form.maxNet, 'exposure.max_net'),
      max_order_notional: {
        amount: Number.isNaN(orderAmount) ? '' : moneyString(orderAmount),
        ccy: 'USD',
      },
      max_orders_per_day: Number.isNaN(maxOrders) ? 0 : maxOrders,
      max_turnover_per_day: pct(form.maxTurnover, 'exposure.max_turnover_per_day'),
    },
    loss: {
      daily_loss_limit: pct(form.dailyLoss, 'loss.daily_loss_limit'),
      drawdown_ladder: [
        { at: pct(form.rung1At, 'loss.drawdown_ladder[0].at'), action: 'shrink', scale },
        { at: pct(form.rung2At, 'loss.drawdown_ladder[1].at'), action: 'halt_flatten' },
      ],
      resume_after_halt: 'human_only',
    },
    autonomy: {
      level: form.level,
      may,
      needs_confirmation: needsConfirmation,
      never: [...REQUIRED_NEVER],
    },
    reporting: {
      digest: form.digest,
      alert_channels: [...form.alertChannels],
    },
  };
  return { body, issues };
}

/** Wire body -> form, for showing an existing mandate or starting a new version from it. */
export function bodyToForm(body: MandateBody): MandateFormState {
  const capital = Number(body.capital.allocated.amount);
  const orderAmount = Number(body.exposure.max_order_notional.amount);
  const rung1 = body.loss.drawdown_ladder[0];
  const rung2 = body.loss.drawdown_ladder[body.loss.drawdown_ladder.length - 1];
  const policies: Record<string, ActionPolicy> = {};
  for (const opt of AGENT_ACTION_OPTIONS) {
    policies[opt.id] = body.autonomy.may.includes(opt.id)
      ? 'may'
      : body.autonomy.needs_confirmation.includes(opt.id)
        ? 'ask'
        : 'deny';
  }
  const caps: Record<string, string> = {};
  for (const [k, v] of Object.entries(body.exposure.max_asset_class)) caps[k] = fractionToPercent(v);
  return {
    template: 'custom',
    jurisdiction: body.basis.jurisdiction,
    capital: Number.isFinite(capital) ? String(capital) : '',
    minCashReserve: fractionToPercent(body.capital.min_cash_reserve),
    venues: [...body.universe.venues],
    assetClasses: [...body.universe.asset_classes],
    instrumentAllow: body.universe.instrument_allow.join(', '),
    shorting: body.universe.shorting,
    derivatives: body.universe.derivatives,
    leverage: String(body.universe.leverage_max_gross),
    maxPosition: fractionToPercent(body.exposure.max_position),
    assetClassCaps: caps,
    maxGross: fractionToPercent(body.exposure.max_gross),
    maxNet: fractionToPercent(body.exposure.max_net),
    maxOrderPct:
      Number.isFinite(capital) && capital > 0 && Number.isFinite(orderAmount)
        ? fractionToPercent(orderAmount / capital)
        : '',
    maxOrdersPerDay: String(body.exposure.max_orders_per_day),
    maxTurnover: fractionToPercent(body.exposure.max_turnover_per_day),
    dailyLoss: fractionToPercent(body.loss.daily_loss_limit),
    rung1At: rung1 ? fractionToPercent(rung1.at) : '',
    rung1Scale: rung1?.scale != null ? fractionToPercent(rung1.scale) : '',
    rung2At: rung2 ? fractionToPercent(rung2.at) : '',
    level: body.autonomy.level,
    policies,
    digest: body.reporting.digest,
    alertChannels: [...body.reporting.alert_channels],
  };
}

// ---------------------------------------------------------------------------
// Plain-language previews (dollars at the stated capital)
// ---------------------------------------------------------------------------

export interface RungPreview {
  /** Dollars below the high point of the account at the stated capital, or NaN. */
  dollars: number;
  text: string;
}

function belowPeak(percentText: string, capital: number): { pct: number; dollars: number } {
  const f = percentToFraction(percentText);
  return { pct: plainNumber(percentText), dollars: Number.isNaN(f) || Number.isNaN(capital) ? Number.NaN : dollarsOf(capital, f) };
}

/** What the agent does when equity falls this far below its highest value, in dollars. */
export function shrinkRungPreview(form: MandateFormState): RungPreview {
  const capital = capitalOf(form);
  const { pct, dollars } = belowPeak(form.rung1At, capital);
  const keep = plainNumber(form.rung1Scale);
  if (Number.isNaN(dollars)) {
    return { dollars, text: 'Enter your capital and a percentage to see this in dollars.' };
  }
  const what =
    keep === 50
      ? 'the agent halves every position'
      : Number.isNaN(keep)
        ? 'the agent shrinks every position'
        : `the agent cuts every position to ${keep}% of its size`;
  return {
    dollars,
    text: `A ${pct}% fall from your account's highest value is ${formatUsd(dollars)} at ${formatUsd(capital)}. ${capitalize(what)}.`,
  };
}

export function haltRungPreview(form: MandateFormState): RungPreview {
  const capital = capitalOf(form);
  const { pct, dollars } = belowPeak(form.rung2At, capital);
  if (Number.isNaN(dollars)) {
    return { dollars, text: 'Enter your capital and a percentage to see this in dollars.' };
  }
  return {
    dollars,
    text: `A ${pct}% fall is ${formatUsd(dollars)} at ${formatUsd(capital)}. Everything is sold and trading stops until you resume it.`,
  };
}

export function dailyLossPreview(form: MandateFormState): RungPreview {
  const capital = capitalOf(form);
  const { pct, dollars } = belowPeak(form.dailyLoss, capital);
  if (Number.isNaN(dollars)) {
    return { dollars, text: 'Enter your capital and a percentage to see this in dollars.' };
  }
  return {
    dollars,
    text: `Losing ${pct}% in one day is ${formatUsd(dollars)} at ${formatUsd(capital)}. Trading stops for the day and a person must resume it.`,
  };
}

/** "25% of your account" in dollars, e.g. maxPosition or an asset-class cap. */
export function shareOfCapitalPreview(percentText: string, capital: number): string {
  const f = percentToFraction(percentText);
  if (Number.isNaN(f) || Number.isNaN(capital)) return '';
  return `about ${formatUsd(dollarsOf(capital, f))}`;
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
}

// ---------------------------------------------------------------------------
// Broker equity check (SPEC section 2: allocated <= broker equity at grant time)
// ---------------------------------------------------------------------------

export type EquityCheck =
  | { level: 'ok' }
  | { level: 'warn'; message: string }
  | { level: 'reject'; message: string };

/** Above the account's equity warns; above 1.05x rejects. */
export function checkCapitalAgainstEquity(capital: number, equity: number): EquityCheck {
  if (!Number.isFinite(capital) || !Number.isFinite(equity) || equity <= 0) return { level: 'ok' };
  if (capital > equity * 1.05) {
    return {
      level: 'reject',
      message: `You entered ${formatUsd(capital)} but the account holds ${formatUsd(equity)}. Enter an amount the account can cover.`,
    };
  }
  if (capital > equity) {
    return {
      level: 'warn',
      message: `${formatUsd(capital)} is slightly more than the ${formatUsd(equity)} the account holds now.`,
    };
  }
  return { level: 'ok' };
}

// ---------------------------------------------------------------------------
// Violation wording for people
// ---------------------------------------------------------------------------

const PLAIN_NAMES: Array<[RegExp, string]> = [
  [/universe\.leverage_max_gross/g, 'the leverage limit'],
  [/exposure\.max_gross/g, 'the total exposure limit'],
  [/exposure\.max_net/g, 'the net exposure limit'],
  [/universe\.asset_classes/g, 'the selected asset classes'],
  [/capital\.allocated\.ccy/g, 'the capital currency'],
  [/capital\.allocated/g, 'the allocated capital'],
  [/instrument_allow/g, 'the allowed instruments'],
  [/halt_flatten/g, 'shut-off'],
];

/**
 * Rewrite a Rust-validator message for the form. The form stores percents, so
 * "10 is above 1: ratios are fractions" would confuse; those become "100% or
 * less". Internal field names are swapped for plain words.
 */
export function plainMessage(v: Violation): string {
  if (v.message.includes('ratios are fractions')) return 'Must be 100% or less.';
  if (v.message.includes('a fraction such as 0.10')) return 'Must be more than 0%.';
  if (v.message.includes('a fraction from 0 up to')) return 'Must be at least 0% and less than 100%.';
  if (v.field === 'exposure.max_position' && v.message.includes('above the gross limit')) {
    return 'Cannot be more than the total exposure limit.';
  }
  let m = v.message;
  for (const [re, plain] of PLAIN_NAMES) m = m.replace(re, plain);
  return m.charAt(0).toUpperCase() + m.slice(1) + (m.endsWith('.') ? '' : '.');
}

/**
 * Merge the form's own issues and the validator's violations into one message
 * per field (the first wins; typing problems come before rule problems).
 */
export function fieldMessages(issues: Violation[], violations: Violation[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const i of issues) if (!(i.field in out)) out[i.field] = i.message;
  for (const v of violations) if (!(v.field in out)) out[v.field] = plainMessage(v);
  return out;
}
